/**
 * O registry PARCIAL do atendimento — spec-010 do `project-plataforma-por-chat`,
 * [T017] / US-2 e US-3.
 *
 * Três cartões que o consultor hoje só vê abrindo o menu passam a ser
 * desenháveis DENTRO da conversa, sem que o `chat-message.vue` saiba que o
 * atendimento existe: o backend manda `ui.component: 'atendimento/recentes'`, o
 * `chat-message` resolve a chave no `UI_REGISTRY` e monta. Cartão novo do
 * módulo é uma entrada aqui — nunca uma linha na bolha.
 *
 * ## Como cada peça se encaixa
 *
 * | peça | onde mora | por quê |
 * |---|---|---|
 * | `component` | `ia/cartoes/*.js` | embrulho que traduz `ui.props` em props |
 * | `schema` | `ui-registry/schemas/` | CÓPIA byte a byte do `contratos/` do backend |
 * | tool → chave | `ui.component`, do backend | as três tools declaram `ui` (T009/T010/T011) |
 *
 * ★ Estes cartões NÃO entram no `LEGACY_TOOL_TO_UI`. Aquele mapa existe para a
 * mensagem persistida ANTES do contrato e para a tool que ainda não declara
 * `ui`; as três tools desta pasta declaram, e `ui.component` tem precedência.
 * Uma entrada lá seria uma segunda fonte da mesma verdade, calada quando
 * divergisse.
 *
 * ★ O `component` NÃO aponta para o `.vue`. Aponta para o embrulho, porque o
 * fio é snake_case (`nao_lidas`, `de_mim`, `due_date`) e os componentes
 * reusados falam o vocabulário deles (`mensagens_nao_lidas`, `isOutgoing`,
 * `dueDate`) — ver `ia/adaptadores.js`. Apontar direto deixaria o cartão
 * montado e VAZIO, que é a falha calada que este contrato existe para evitar.
 *
 * ## Duas decisões que não são cópia
 *
 * **`module` é o do CRM, não um do atendimento.** O catálogo de navegação
 * (`ROUTE_KEYS`) não tem chave para o atendimento: a tela é uma ABA do CRM
 * (`crm.atendimento`, em `modules/crm/routes.js`), e é o CRM que aparece no
 * menu. Inventar `'atendimento'` aqui poria no registry um módulo que o
 * catálogo não resolve.
 *
 * **A `feature` NÃO é a mesma nos três.** Os dois cartões de conversa
 * (`recentes`, `mensagens-do-cliente`) usam `FEATURES.ATENDIMENTO`, a flag da
 * própria tela — a mesma do `meta.can_show` da rota e do `PERMISSION` do
 * módulo: escritório sem o omnichannel contratado não pode ver a caixa de
 * WhatsApp dentro de uma conversa, e o backend da tool da caixa gateia do
 * mesmo jeito (fail-closed dos dois lados). Já o cartão de ALERTAS usa
 * `FEATURES.CRM`, porque o que ele mostra é tarefa/oportunidade/atividade —
 * ver a nota em `FLAG_DOS_ALERTAS`.
 *
 * Sem `?.`/`??` (o Babel deste projeto não transpila). Acentos UTF-8 reais.
 */
import { FEATURES } from '@constants/Text'
import { ROUTE_KEYS } from '@constants/navigation-routes'

import { CHAVES } from './chaves'

export {
  CHAVES,
  ROTA_DO_ATENDIMENTO,
  TOOL_ALERTAS,
  TOOL_CONVERSAS_DA_CAIXA,
  TOOL_MENSAGENS_DO_CLIENTE,
} from './chaves'

/** A tela do atendimento é uma aba do CRM — ver o cabeçalho. */
const MODULO = ROUTE_KEYS.CRM

/**
 * A flag da tela de atendimento (`meta.can_show` da rota `crm.atendimento`) —
 * usada pelos DOIS cartões que mostram conversa do omnichannel.
 */
const FLAG_DO_ATENDIMENTO = FEATURES.ATENDIMENTO

/**
 * ★ A flag dos ALERTAS é a do CRM, e a diferença não é detalhe (spec-010,
 * achado P1 do caso 2 da [T023]).
 *
 * O gate do registry pergunta *este escritório pode ver ESTE conteúdo?*, e o
 * conteúdo do cartão de alertas é tarefa, oportunidade e atividade — nada de
 * atendimento. A tool `list_alertas_atrasados` não tem gate no backend
 * justamente por isso. Gateá-lo por `FEATURES.ATENDIMENTO` recusava o cartão
 * para o escritório que TEM o dado e não contratou o omnichannel: o consultor
 * perguntava "o que está atrasado?", a tool respondia, e o thread mostrava
 * `fallback-card` com motivo `flag_desligada` — medido no 83.
 *
 * O `module` continua sendo o do CRM (a tela é a aba `crm.atendimento`); o que
 * muda é só a pergunta que o registry faz antes de montar.
 */
const FLAG_DOS_ALERTAS = FEATURES.CRM

/**
 * Enquanto a tool roda, e quando o resultado chega.
 *
 * `input-available` entra nos três: é o estado em que a parte vive enquanto a
 * tool consulta, e recusá-lo faria aparecer um cartão de ERRO no meio de todo
 * turno normal, trocado pelo certo um segundo depois. Os dois desfechos de
 * erro (`output-error`, `output-denied`) ficam de fora de propósito — nenhum
 * dos três sabe desenhar uma falha, e um cartão que renderiza nada é pior que
 * o `fallback-card` dizendo o que aconteceu. `approval-requested` não se
 * aplica: os três são leitura, não autorizam nada.
 */
const ESTADOS_DE_LEITURA = ['input-available', 'output-available']

export default {
  [CHAVES.ALERTAS]: {
    component: () =>
      import('@modules/atendimento/ia/cartoes/alertas-atrasados'),
    schema: () =>
      import(
        '@modules/aawz-intelligence-v2/ui-registry/schemas/ui-atendimento-alertas-atrasados.schema.json'
      ),
    module: MODULO,
    feature: FLAG_DOS_ALERTAS,
    states: ESTADOS_DE_LEITURA,
    // Os rótulos são os `LABEL` das tools no backend, à letra: desde a D10 o
    // `tool-call-badge` lê `entrada.label` ANTES do `TOOL_LABELS`, então um
    // texto diferente aqui faria o badge dizer uma coisa e o fio outra.
    label: 'Consultando alertas...',
  },
  [CHAVES.RECENTES]: {
    component: () => import('@modules/atendimento/ia/cartoes/recentes'),
    schema: () =>
      import(
        '@modules/aawz-intelligence-v2/ui-registry/schemas/ui-atendimento-recentes.schema.json'
      ),
    module: MODULO,
    feature: FLAG_DO_ATENDIMENTO,
    states: ESTADOS_DE_LEITURA,
    label: 'Consultando atendimentos...',
  },
  [CHAVES.MENSAGENS]: {
    component: () =>
      import('@modules/atendimento/ia/cartoes/mensagens-do-cliente'),
    schema: () =>
      import(
        '@modules/aawz-intelligence-v2/ui-registry/schemas/ui-atendimento-mensagens-do-cliente.schema.json'
      ),
    module: MODULO,
    feature: FLAG_DO_ATENDIMENTO,
    states: ESTADOS_DE_LEITURA,
    label: 'Consultando atendimentos do cliente...',
  },
}
